import { useQueryClient } from '@tanstack/react-query';
import { useSearchParameters } from 'hooks/useSearchParameters';
import { queryKeys, sdk, assertAffectedRecord, checkContainsId, API_FUNCTIONS, RULES_ENDPOINTS } from 'utils/query';
import { useTranslation } from 'react-i18next';
import { ApiTaskAffectedRecords, ApiTaskResult, DocumentIdProps } from 'models/general';
import { EmptyParametersError } from 'services/errors/EmptyParametersError';
import { useCustomMutation } from '../useCustomMutation';

const updateWorklistNote = async (
  variables: {
    noteId?: string;
    text?: string;
    userId?: string;
  } & DocumentIdProps,
) => {
  const { documentId, noteId, text, userId } = variables;
  if (!documentId || !userId || (!noteId && !text)) throw new EmptyParametersError();

  const notesUrl = `${RULES_ENDPOINTS.monitoringNotifications}/${documentId}/notes`;

  if (noteId && text) {
    const result = await sdk.tasks.api.put<ApiTaskAffectedRecords, { text: string }>(
      API_FUNCTIONS.monitoring,
      `${notesUrl}/${noteId}`,
      { text },
      {},
    );
    return assertAffectedRecord({ result });
  }
  if (!text && noteId) {
    const result = await sdk.tasks.api.delete<ApiTaskAffectedRecords>(
      API_FUNCTIONS.monitoring,
      `${notesUrl}/${noteId}`,
      {},
    );
    return assertAffectedRecord({ result });
  }

  const result = await sdk.tasks.api.post<ApiTaskResult, { text?: string }>(
    API_FUNCTIONS.monitoring,
    notesUrl,
    { text },
    {},
  );
  return checkContainsId({ result });
};

export const useUpdateWorklistNote = () => {
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const { parameters: filters } = useSearchParameters(queryKeys.worklistOverview);

  return useCustomMutation({
    fn: updateWorklistNote,
    onSuccess: {
      message: t('update_note_notification'),
      invalidateQueryKeys: [[queryKeys.worklistOverview, filters]],
      callback: async (_result, variables) => {
        await queryClient.invalidateQueries({ queryKey: [queryKeys.worklistRecord, variables.documentId] });
      },
    },
  });
};
